import { Kind, visit } from "graphql/language";

const cache = new WeakMap();

const responseKey = (field) => field.alias?.value ?? field.name.value;

function buildFieldTree(fields) {
  const root = new Map();
  for (const path of fields) {
    const segments = path.split(".");
    let level = root;
    segments.forEach((segment, index) => {
      if (!level.has(segment)) {
        level.set(segment, {
          path: segments.slice(0, index + 1).join("."),
          complete: false,
          children: new Map(),
        });
      }
      const node = level.get(segment);
      if (index === segments.length - 1) {
        node.complete = true;
      }
      level = node.children;
    });
  }
  return root;
}

function markMatched(node, matchedPaths) {
  matchedPaths.add(node.path);
  node.children.forEach((child) => markMatched(child, matchedPaths));
}

function pruneSelectionSet(selectionSet, tree, context) {
  const selections = selectionSet.selections
    .map((selection) => pruneSelection(selection, tree, context))
    .filter(Boolean);
  return selections.length ? { ...selectionSet, selections } : null;
}

function pruneSelection(selection, tree, context) {
  if (selection.kind === Kind.FRAGMENT_SPREAD) {
    const fragment = context.fragments.get(selection.name.value);
    return pruneSelection(
      {
        kind: Kind.INLINE_FRAGMENT,
        typeCondition: fragment.typeCondition,
        directives: selection.directives,
        selectionSet: fragment.selectionSet,
      },
      tree,
      context
    );
  }

  if (selection.kind === Kind.INLINE_FRAGMENT) {
    const selectionSet = pruneSelectionSet(
      selection.selectionSet,
      tree,
      context
    );
    return selectionSet && { ...selection, selectionSet };
  }

  if (selection.name.value === "__typename") {
    return selection;
  }

  const node = tree.get(responseKey(selection));
  if (!node) {
    return null;
  }
  if (node.complete) {
    markMatched(node, context.matchedPaths);
    return selection;
  }
  if (!selection.selectionSet) {
    return null;
  }
  const selectionSet = pruneSelectionSet(
    selection.selectionSet,
    node.children,
    context
  );
  return selectionSet && { ...selection, selectionSet };
}

function collectNames(node, kind) {
  const names = new Set();
  visit(node, {
    [kind]: (found) => {
      names.add(found.name.value);
    },
  });
  return names;
}

function collectUsedFragments(operation, fragments) {
  const used = new Set();
  const pending = [...collectNames(operation, Kind.FRAGMENT_SPREAD)];
  while (pending.length) {
    const name = pending.pop();
    if (!used.has(name)) {
      used.add(name);
      pending.push(...collectNames(fragments.get(name), Kind.FRAGMENT_SPREAD));
    }
  }
  return used;
}

function findField(selectionSet, key) {
  return selectionSet?.selections.find(
    (selection) =>
      selection.kind === Kind.FIELD && responseKey(selection) === key
  );
}

function replaceSelection(node, oldSelection, newSelection) {
  return {
    ...node,
    selectionSet: {
      ...node.selectionSet,
      selections: node.selectionSet.selections.map((selection) =>
        selection === oldSelection ? newSelection : selection
      ),
    },
  };
}

function select(document, fields, itemsKey) {
  const operation = document.definitions.find(
    (definition) => definition.kind === Kind.OPERATION_DEFINITION
  );
  const rootField = operation?.selectionSet.selections.find(
    (selection) => selection.kind === Kind.FIELD
  );
  const itemsField = findField(rootField?.selectionSet, itemsKey);
  if (!itemsField?.selectionSet) {
    throw new Error(
      `selectDataTableFields: "${operation?.name?.value}" has no "${itemsKey}" selection under its root field`
    );
  }

  const fragments = new Map(
    document.definitions
      .filter((definition) => definition.kind === Kind.FRAGMENT_DEFINITION)
      .map((definition) => [definition.name.value, definition])
  );
  const context = { fragments, matchedPaths: new Set() };

  const itemsSelectionSet = pruneSelectionSet(
    itemsField.selectionSet,
    buildFieldTree(fields),
    context
  ) ?? {
    kind: Kind.SELECTION_SET,
    selections: [
      { kind: Kind.FIELD, name: { kind: Kind.NAME, value: "__typename" } },
    ],
  };

  const prunedOperation = replaceSelection(
    operation,
    rootField,
    replaceSelection(rootField, itemsField, {
      ...itemsField,
      selectionSet: itemsSelectionSet,
    })
  );

  const usedFragments = collectUsedFragments(prunedOperation, fragments);
  const fragmentDefinitions = document.definitions.filter(
    (definition) =>
      definition.kind === Kind.FRAGMENT_DEFINITION &&
      usedFragments.has(definition.name.value)
  );

  const usedVariables = collectNames(
    [
      prunedOperation.selectionSet,
      ...(prunedOperation.directives ?? []),
      ...fragmentDefinitions,
    ],
    Kind.VARIABLE
  );

  return {
    document: {
      kind: Kind.DOCUMENT,
      definitions: [
        {
          ...prunedOperation,
          variableDefinitions: prunedOperation.variableDefinitions?.filter(
            (definition) => usedVariables.has(definition.variable.name.value)
          ),
        },
        ...fragmentDefinitions,
      ],
    },
    unmatchedFields: fields.filter((field) => !context.matchedPaths.has(field)),
  };
}

export function selectDataTableFields(
  documentModule,
  fields,
  { itemsKey = "items" } = {}
) {
  const document = documentModule.default || documentModule;
  const uniqueFields = [...new Set(fields)].sort();
  const cacheKey = `${itemsKey}:${uniqueFields.join(",")}`;

  if (!cache.has(document)) {
    cache.set(document, new Map());
  }
  const documentCache = cache.get(document);
  if (!documentCache.has(cacheKey)) {
    documentCache.set(cacheKey, select(document, uniqueFields, itemsKey));
  }
  return documentCache.get(cacheKey);
}
